import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Factory, PackageCheck, X, RefreshCw, Maximize2, Minimize2, CalendarDays } from "lucide-react";

const clockStr = (d = new Date()) =>
  d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const dateStr = () =>
  new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(new Date());

function Card({ title, count, tone, children, testid }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-lg overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10"
           style={{ background: tone }}>
        <div className="font-display font-black text-2xl text-white">{title}</div>
        <div className="font-mono text-white/80 text-xl">{count}</div>
      </div>
      <div className="p-6 space-y-3">
        {children}
      </div>
    </div>
  );
}

function Row({ number, label, sub, right }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-white/5 last:border-b-0">
      <div className="font-mono text-white/60 text-sm w-28 shrink-0">{number}</div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-2xl text-white truncate">{label}</div>
        {sub && <div className="text-white/60 text-sm">{sub}</div>}
      </div>
      {right && <div className="text-right font-mono text-lg text-[var(--pop-pink)]">{right}</div>}
    </div>
  );
}

function ProductionMonitor() {
  const [data, setData] = useState(null);
  const load = () => api.get("/monitor/production").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const active = data?.active || [];
  const planned = data?.planned || [];
  const done = data?.completed_today || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
      <Card title="Läuft gerade" count={active.length} tone="linear-gradient(135deg,#EB75B5,#c94b95)" testid="mon-active">
        {active.length === 0 && <div className="text-white/50 text-lg py-8 text-center">Keine aktive Produktion.</div>}
        {active.map((o) => (
          <Row key={o.id} number={o.number}
               label={o.output_item_name}
               sub={`${o.recipe_name} · ${o.output_item_sku}`}
               right={`${o.planned_quantity ?? "—"} ${o.output_unit || ""}`} />
        ))}
      </Card>
      <Card title="Geplant" count={planned.length} tone="linear-gradient(135deg,#3b82f6,#1e3a8a)" testid="mon-planned">
        {planned.length === 0 && <div className="text-white/50 text-lg py-8 text-center">Nichts eingeplant.</div>}
        {planned.map((o) => (
          <Row key={o.id} number={o.number}
               label={o.output_item_name}
               sub={`${o.recipe_name} · ${o.planned_date?.slice(0, 10) || "—"}`}
               right={`${o.planned_quantity ?? "—"} ${o.output_unit || ""}`} />
        ))}
      </Card>
      <Card title="Heute fertig" count={done.length} tone="linear-gradient(135deg,#10b981,#047857)" testid="mon-done">
        {done.length === 0 && <div className="text-white/50 text-lg py-8 text-center">Noch nichts abgeschlossen.</div>}
        {done.map((o) => (
          <Row key={o.number} number={o.number}
               label={`${o.actual_quantity ?? "—"}`}
               sub={`Charge ${o.output_charge}`}
               right={o.completed_at?.slice(11, 16)} />
        ))}
      </Card>
    </div>
  );
}

function WarehouseMonitor() {
  const [data, setData] = useState(null);
  const load = () => api.get("/monitor/warehouse").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const picks = data?.picks || [];
  const receipts = data?.receipts || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
      <Card title="Zu kommissionieren" count={picks.length} tone="linear-gradient(135deg,#EB75B5,#c94b95)" testid="mon-picks">
        {picks.length === 0 && <div className="text-white/50 text-lg py-8 text-center">Nichts offen.</div>}
        {picks.map((p) => (
          <Row key={p.id} number={p.number}
               label={p.customer_name}
               sub={`Tour ${p.tour || "—"} · ${p.lines_count} Positionen`}
               right={p.delivery_date?.slice(0, 10) || ""} />
        ))}
      </Card>
      <Card title="Wareneingang erwartet" count={receipts.length} tone="linear-gradient(135deg,#d97706,#7c2d12)" testid="mon-receipts">
        {receipts.length === 0 && <div className="text-white/50 text-lg py-8 text-center">Nichts angekündigt.</div>}
        {receipts.map((r) => (
          <Row key={r.id} number={r.number}
               label={r.supplier_name}
               sub={`${r.lines_count} Positionen`}
               right={r.expected_date?.slice(0, 10) || ""} />
        ))}
      </Card>
    </div>
  );
}

const WEEKDAY_LABELS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const TYPE_COLORS = {
  production: "#EB75B5",
  delivery: "#3b82f6",
  order: "#10b981",
};

function WeekMonitor() {
  const [data, setData] = useState(null);
  const load = () => api.get("/monitor/week").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const columns = data?.columns || [];
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex-1 min-h-0 overflow-auto" data-testid="mon-week">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 h-full">
        {columns.map((col, idx) => {
          const isToday = col.date === todayKey;
          const dayDate = new Date(col.date + "T12:00:00");
          return (
            <div
              key={col.date}
              className={`flex flex-col rounded-2xl bg-white/5 border ${isToday ? "border-[var(--pop-pink)]" : "border-white/10"} min-h-[420px] overflow-hidden`}
              data-testid={`week-col-${col.date}`}
            >
              <div className={`px-4 py-3 border-b border-white/10 ${isToday ? "bg-[var(--pop-pink)]/20" : "bg-white/5"}`}>
                <div className="text-white/60 text-xs">{WEEKDAY_LABELS[idx]}</div>
                <div className={`font-display font-black text-2xl ${isToday ? "text-[var(--pop-pink)]" : "text-white"}`}>
                  {dayDate.getDate()}.{dayDate.getMonth() + 1}
                </div>
                <div className="text-white/40 text-xs mt-1">{col.events.length} Termine</div>
              </div>
              <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                {col.events.length === 0 && (
                  <div className="text-white/30 text-xs italic text-center pt-6">frei</div>
                )}
                {col.events.map((e) => (
                  <div
                    key={`${e.type}-${e.id}`}
                    data-testid={`week-event-${e.ref}`}
                    className="rounded-xl bg-white/10 p-3 border-l-4"
                    style={{ borderColor: TYPE_COLORS[e.type] || "#888" }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="pop-pill text-[10px]" style={{ background: TYPE_COLORS[e.type], color: "#fff" }}>
                        {e.type === "production" ? "PROD" : e.type === "delivery" ? "LIEF" : "BEST"}
                      </span>
                      <span className="font-mono text-xs text-white/60">{e.ref}</span>
                    </div>
                    <div className="mt-2 font-semibold text-white leading-tight">{e.title}</div>
                    {e.sub && <div className="text-xs text-white/60 mt-0.5">{e.sub}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Monitor() {
  const { kind = "produktion" } = useParams();
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());
  const [isFull, setIsFull] = useState(false);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Keep screen awake while monitor page is open
  useEffect(() => {
    let released = false;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (_e) {}
    };
    acquire();
    const onVis = () => {
      if (document.visibilityState === "visible" && !released) acquire();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVis);
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
    };
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (_e) {}
  };

  const isProd = kind === "produktion";
  const isWeek = kind === "woche";
  const isWarehouse = kind === "lager";

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] text-white flex flex-col p-6 md:p-10 overflow-hidden" data-testid="page-monitor">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img src="/logos/logo-white.png" alt="POPCRN" className="h-8 md:h-10 w-auto" />
          <div className="pl-4 border-l border-white/20">
            <div className="overline text-white/50">Monitor</div>
            <div className="font-display font-black text-2xl md:text-3xl">
              {isProd ? "Produktion" : isWeek ? "Wochenplan" : "Lager"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex flex-col items-end">
            <div className="font-mono text-3xl md:text-4xl font-bold">{clockStr(now)}</div>
            <div className="text-white/60 text-sm">{dateStr()}</div>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-white/10 p-1">
            <button
              data-testid="btn-monitor-produktion"
              onClick={() => nav("/monitor/produktion")}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold ${isProd ? "bg-[var(--pop-pink)] text-black" : "text-white/70"}`}
            ><Factory size={14} className="inline mr-1" /> Produktion</button>
            <button
              data-testid="btn-monitor-woche"
              onClick={() => nav("/monitor/woche")}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold ${isWeek ? "bg-[var(--pop-pink)] text-black" : "text-white/70"}`}
            ><CalendarDays size={14} className="inline mr-1" /> Woche</button>
            <button
              data-testid="btn-monitor-lager"
              onClick={() => nav("/monitor/lager")}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold ${isWarehouse ? "bg-[var(--pop-pink)] text-black" : "text-white/70"}`}
            ><PackageCheck size={14} className="inline mr-1" /> Lager</button>
          </div>
          <Button
            data-testid="btn-monitor-fullscreen"
            onClick={toggleFullscreen}
            variant="outline"
            title={isFull ? "Vollbild verlassen" : "Vollbild"}
            className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">
            {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </Button>
          <Button
            data-testid="btn-monitor-exit"
            onClick={() => nav("/")}
            variant="outline"
            className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">
            <X size={16} />
          </Button>
        </div>
      </header>

      {isProd ? <ProductionMonitor /> : isWeek ? <WeekMonitor /> : <WarehouseMonitor />}

      <footer className="mt-6 text-white/40 text-xs flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RefreshCw size={12} /> Aktualisiert automatisch alle 30 Sekunden
        </div>
        <div className="md:hidden font-mono">{clockStr(now)}</div>
      </footer>
    </div>
  );
}
